<template>
  <div class="country_analytics">
    <span class="mode_selector">
      <button type="button" class="left" :class="{ on: mode === 'map' }"
        @click="mode = 'map'">
        <span class="icon icon--location"></span>Map
      </button>
      <button type="button" class="right" :class="{ on: mode === 'list' }"
        @click="mode = 'list'">
        <span class="icon icon--list"></span>List
      </button>
    </span>
    <div v-show="mode === 'list'" class="list_box">
      <table class="list_table">
        <tr>
          <th class="rank">{{ $t('common.rank') }}</th>
          <th class="rate">{{ $t('analytics.content.visitPerCountry.visitRate') }}</th>
        </tr>
        <tr v-for="(item, index) in listItems" :key="index">
          <td class="rank">
            <span class="no">{{ index + 1 }}</span>
            <template v-if="item.code === 'etc'">
              <i class="flag icon icon--etc"></i>
            </template>
            <country-flag v-else
              class="flag"
              :countryCode="item.code"
              :countryName="item.name"/>
            <span class="name">{{ item.name }}</span>
          </td>
          <td class="rate">{{ item.percentage | fixed(1) }}%</td>
        </tr>
      </table>
    </div>
    <div v-show="mode === 'map'">
      <app-chart chartType="map"
        :chartOptions="chartOptions"
        :height="300">
      </app-chart>
      <div class="control">
        <button type="button" class="btn_plus" @click="setZoom(true)">
          <span class="icon icon--plus"></span>
        </button>
        <button type="button" class="btn_minus" @click="setZoom(false)">
          <span class="icon icon--minus"></span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { sum, getPercentage } from '@/libs/utils'
import { getTooltipHtml } from '@/libs/chart-helper'
import CountryFlag from '@/components/ui/CountryFlag'
import AppChart from '@/components/chart'

const MAX_ZOOM = 10
const MIN_ZOOM = 1

export default {
  name: 'CountryAnalytics',
  components: {
    AppChart,
    CountryFlag
  },
  props: [
    'rawData',
    'countryData'
  ],
  data () {
    return {
      mode: 'map',
      zoom: 1
    }
  },
  computed: {
    chartOptions () {
      const total = this.rawData.uvTotal

      let minVisit = this.countryData[0].value
      let maxVisit = minVisit
      for (let i = 1; i < this.countryData.length; i++) {
        if (minVisit > this.countryData[i].value) {
          minVisit = this.countryData[i].value
        }
        if (maxVisit < this.countryData[i].value) {
          maxVisit = this.countryData[i].value
        }
      }
      if (minVisit === maxVisit) {
        minVisit = maxVisit - 1
      }

      return {
        series: [
          {
            type: 'map',
            mapType: 'world',
            roam: 'move',
            left: 10,
            right: 10,
            top: 60,
            zoom: this.zoom,
            data: this.countryData,
            markPoint: {
              symbolSize: 0
            },
            label: {
              emphasis: {
                show: false
              }
            },
            itemStyle: {
              areaColor: '#ebebeb',
              borderColor: '#ebebeb',
              emphasis: {
                areaColor: '#ebebeb',
                borderColor: '#aaa',
                color: 'transparent'
              }
            }
          }
        ],
        tooltip: {
          formatter (param) {
            const percentage = getPercentage(param.value, total, 1)
            const content = [
              [param.name, `${percentage}%`]
            ]
            return getTooltipHtml(content)
          }
        },
        visualMap: {
          show: false,
          min: minVisit,
          max: maxVisit
        }
      }
    },
    listItems () {
      const allItems = this.countryData
      const total = this.rawData.uvTotal
      allItems.sort((a, b) => {
        return a.value >= b.value ? -1 : 1
      })
      const topItems = allItems.slice(0, 5).map(item => {
        return {
          ...item,
          percentage: getPercentage(item.value, total)
        }
      })

      const etcItems = allItems.slice(5)

      if (etcItems.length) {
        const etcTotal = sum(etcItems, 'value')
        topItems.push({
          name: 'etc',
          value: etcTotal,
          code: 'etc',
          percentage: getPercentage(etcTotal, total)
        })
      }

      return topItems
    }
  },
  methods: {
    setZoom (isPlus) {
      const step = isPlus ? 1 : -1
      const zoom = this.zoom + step

      if (zoom > MAX_ZOOM || zoom < MIN_ZOOM) {
        return
      }
      this.zoom = zoom
    }
  }
}
</script>

<style lang="scss">
.country_analytics {
  position: relative;

  .mode_selector {
    position: absolute;
    right: -10px;
    top: -48px;
    display: inline-block;
    vertical-align: middle;
    height: 32px;
    border-radius: 16px;
    box-shadow: inset 0 1px 1px 0 rgba(0, 0, 0, 0.04);
    border: solid 1px #d7d7d7;
    background-color: rgba(0, 0, 0, 0.06);

    &> button {
      display: inline-block;
      box-sizing: border-box;
      height: 100%;
      line-height: 32px;
      padding: 0 10px 0 8px;
      font-weight: bold;
      color: #666;
      background-color: transparent;
      cursor: pointer;
    }

    .icon {
      vertical-align: top;
      margin-top: 8px;
      margin-right: 4px;
    }

    .on {
      border-radius: 16px;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
      background-color: #ffffff;
      cursor: default;
    }

    .left.on {
      margin-right: -4px;
    }
    .right.on {
      margin-left: -4px;
    }
  }

  .control {
    position: absolute;
    top: 60px;
    left: 25px;
    width: 34px;

    button {
      display: block;
      background-color: #fff;
      width: 100%;
      height: 34px;
      border-radius: 20px;
      box-sizing: border-box;
      padding-top: 3px;
      margin-bottom: 8px;
      box-shadow: 0 6px 10px -3px rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
    }
  }

  .list_box {
    padding-top: 24px;
  }

  .list_table {
    border-collapse: collapse;
    width: 100%;
    text-align: center;

    th {
      font-size: 12px;
      color: #444;
      padding-bottom: 6px;
    }

    td {
      font-size: 13px;
      color: #2f2f2f;
      padding: 12px 0 10px 0;
    }

    tr {
      border-bottom: 1px dashed #f4f4f1;

      &:first-child {
        border-bottom: 1px solid #bbb;
      }

      &:last-child {
        border-bottom: 1px solid #d7d7d7;
      }
    }

    .rank {
      text-align: left;
      padding-left: 16px;

      .no { margin-right: 22px;}

      .flag {
        position: relative;
        top: -1px;
        margin-right: 8px;
      }
      .icon--etc {
        vertical-align: middle;
      }
    }
    .rate {
      text-align: right;
      padding-right: 16px;
    }
  }
}
</style>
